/**
 * Format a date string to a more readable format
 * @param dateString ISO date string
 * @returns Formatted date string
 */
export const formatDate = (dateString: string): string => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return '';
  }
  
  // Format options
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  };
  
  return new Intl.DateTimeFormat('en-US', options).format(date);
};

/**
 * Get relative time string (e.g., "2 hours ago", "in 3 days")
 * Handles both past and future dates.
 * @param dateString ISO date string
 * @returns Relative time string
 */
export const getRelativeTimeString = (dateString: string): string => {
  if (!dateString) return '';

  const date = new Date(dateString);

  if (isNaN(date.getTime())) {
    return '';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);
  const absSeconds = Math.abs(diffInSeconds);
  const isFuture = diffInSeconds > 0;

  if (absSeconds < 60) {
    return isFuture ? 'in a moment' : 'just now';
  }

  if (absSeconds < 3600) {
    const minutes = Math.floor(absSeconds / 60);
    return isFuture
      ? `in ${minutes} minute${minutes > 1 ? 's' : ''}`
      : `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  }

  if (absSeconds < 86400) {
    const hours = Math.floor(absSeconds / 3600);
    return isFuture
      ? `in ${hours} hour${hours > 1 ? 's' : ''}`
      : `${hours} hour${hours > 1 ? 's' : ''} ago`;
  }

  if (absSeconds < 604800) {
    const days = Math.floor(absSeconds / 86400);
    return isFuture
      ? `in ${days} day${days > 1 ? 's' : ''}`
      : `${days} day${days > 1 ? 's' : ''} ago`;
  }

  return formatDate(dateString);
};

/**
 * Check if a date is in the past
 * @param dateString ISO date string
 * @returns Boolean indicating if date is in the past
 */
export const isDatePast = (dateString: string): boolean => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return false;
  }
  
  return date < new Date();
};

/**
 * Check if a date is today
 * @param dateString ISO date string
 * @returns Boolean indicating if date is today
 */
export const isDateToday = (dateString: string): boolean => {
  if (!dateString) return false;
  
  const date = new Date(dateString);
  
  // Check if date is valid
  if (isNaN(date.getTime())) {
    return false;
  }
  
  const today = new Date();
  
  return (
    date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear()
  );
};